import { ref } from 'vue';
import AgentsAPI from 'dashboard/api/agents';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import SalesDashboardAPI from 'dashboard/api/salesDashboard';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';

export function useSalesData({ t, notify }) {
  const rows = ref([]);
  const addresses = ref([]);
  const agents = ref([]);
  const postSaleStatuses = ref([]);
  const dashboard = ref({
    counts: {},
    recent: [],
    recent_list: 'rascunhos',
    recent_total: 0,
    pending: [],
    pending_charges: [],
  });
  const selectedTransaction = ref(null);
  const loading = ref(false);
  const loadingAddresses = ref(false);
  const loadingDashboard = ref(false);
  const loadingRecent = ref(false);
  const detailLoading = ref(false);
  const listUpdatedAt = ref(null);
  const error = ref('');
  const totalRows = ref(0);
  const totalPages = ref(1);

  const errorMessage = requestError =>
    requestError?.response?.data?.message ||
    requestError?.response?.data?.error ||
    t('SALES.ALERTS.LOAD_ERROR');

  const loadList = async params => {
    loading.value = true;
    listUpdatedAt.value = new Date();
    error.value = '';
    try {
      const { data } = await SalesDashboardAPI.list(params);
      rows.value = data?.data || [];
      totalRows.value = data?.meta?.count || 0;
      totalPages.value = data?.meta?.total_pages || 1;
    } catch (requestError) {
      error.value = errorMessage(requestError);
      rows.value = [];
    } finally {
      loading.value = false;
    }
  };

  const loadDashboard = async (params, recentOnly = false) => {
    const loadingState = recentOnly ? loadingRecent : loadingDashboard;
    loadingState.value = true;
    try {
      const { data } = await SalesDashboardAPI.dashboard(params);
      dashboard.value = {
        counts: {},
        recent: [],
        recent_list: 'rascunhos',
        recent_total: 0,
        pending: [],
        pending_charges: [],
        ...(data || {}),
      };
    } catch (requestError) {
      notify(errorMessage(requestError));
    } finally {
      loadingState.value = false;
    }
  };

  const loadAddresses = async params => {
    loadingAddresses.value = true;
    try {
      const { data } = await SalesDashboardAPI.addresses(params);
      addresses.value = data?.data || [];
    } catch {
      addresses.value = [];
    } finally {
      loadingAddresses.value = false;
    }
  };

  const loadAgents = async () => {
    try {
      const { data } = await AgentsAPI.get();
      agents.value = data?.payload || data || [];
    } catch {
      agents.value = [];
    }
  };

  const loadPostSaleStatuses = async () => {
    try {
      const { data } = await PostSaleCasesAPI.statusCatalog();
      postSaleStatuses.value = (data?.data || []).filter(
        status => !['finalizado', 'cancelado'].includes(String(status.id))
      );
    } catch {
      postSaleStatuses.value = [];
    }
  };

  const loadTransaction = async id => {
    detailLoading.value = true;
    try {
      const { data } = await SalesTransactionsAPI.show(id);
      selectedTransaction.value = data;
      return data;
    } catch (requestError) {
      notify(errorMessage(requestError));
      return null;
    } finally {
      detailLoading.value = false;
    }
  };

  return {
    addresses,
    agents,
    dashboard,
    detailLoading,
    error,
    loadAgents,
    loadAddresses,
    loadDashboard,
    loadList,
    loadPostSaleStatuses,
    loadTransaction,
    loading,
    loadingAddresses,
    loadingDashboard,
    loadingRecent,
    listUpdatedAt,
    postSaleStatuses,
    rows,
    selectedTransaction,
    totalPages,
    totalRows,
  };
}
